import React, { useEffect, useState, useRef } from "react";
import { FiSettings, FiMonitor, FiRefreshCw, FiTrash2 } from "react-icons/fi";

const Process = () => {
  const steps = [
    {
      id: 1,
      title: " Strategy & Pre-Production",
      description:
        "We begin with an in-depth consultation to understand your vision, target audience, and content goals. Our experts assist in scripting, format structuring, and selecting the right technical setup for a flawless recording session.",
      icon: <FiSettings className="w-6 h-6" />,

      position: "left",
      color: "bg-slate-600/20",
      borderColor: "#fdac00",
    },
    {
      id: 2,
      title: "Professional Studio Recording",
      description:
"Step into our state-of-the-art studio, equipped with industry-leading microphones, acoustic treatment, and expert on-site support. We provide a stress-free recording environment, ensuring crystal-clear audio quality for you and your guests.",
      icon: <FiMonitor className="w-6 h-6" />,
      position: "right",
      color: "bg-slate-600/20",
      borderColor: "#fdac00",
    },
    {
      id: 3,
      title: "Precision Editing & Post-Production",
      description:
"Once your recording is complete, our skilled post-production team refines your audio with meticulous editing, noise reduction, equalization, and mastering. The result? A polished, studio-quality podcast ready for your audience.",
      icon: <FiRefreshCw className="w-6 h-6" />,
      position: "left",
      //   color: "bg-blue-gray-600/20",
      color: "bg-slate-600/20",

      //   borderColor: "border-blue-gray-600"
      borderColor: "#fdac00",
    },
    {
      id: 4,
      title: "Final Delivery & Multi-Platform Distribution",
      description:
"We provide high-quality audio files and assist in publishing your podcast across major platforms like Spotify, Apple Podcasts, and YouTube. Need promotional assets? We can create audiograms, transcripts, and social media snippets to maximize your reach",
      icon: <FiTrash2 className="w-6 h-6" />,
      position: "right",
      color: "bg-white-600/20",
      borderColor: "#fdac00",
    },
  ];

  const [lineHeight, setLineHeight] = useState(0);
  const [activeSteps, setActiveSteps] = useState(new Set());
  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const stepRefs = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const updateLineHeight = () => {
      if (!sectionRef.current || !lineRef.current) return;

      const section = sectionRef.current;
      const sectionRect = section.getBoundingClientRect();
      const sectionTop = sectionRect.top + window.scrollY;
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate line height based on scroll position within section
      const startPoint = sectionTop - windowHeight * 0.5;
      const endPoint = sectionTop + section.offsetHeight - windowHeight * 0.5;
      const totalDistance = endPoint - startPoint;

      let progress = 0;
      if (scrollTop > startPoint && scrollTop < endPoint) {
        progress = (scrollTop - startPoint) / totalDistance;
      } else if (scrollTop >= endPoint) {
        progress = 1;
      }

      const newHeight = progress * 100;
      setLineHeight(newHeight);
    };

    const handleScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateLineHeight);
    };

    // Intersection Observer for circles
    const observer = new IntersectionObserver(
      (entries) => {
        const newActiveSteps = new Set(activeSteps);
        entries.forEach((entry) => {
          const index = stepRefs.current.findIndex(
            (ref) => ref === entry.target
          );
          if (entry.isIntersecting) {
            newActiveSteps.add(index);
          } else if (entry.boundingClientRect.top > window.innerHeight * 0.5) {
            newActiveSteps.delete(index);
          }
        });
        setActiveSteps(newActiveSteps);
      },
      {
        root: null,
        threshold: 0.5,
        rootMargin: "0px 0px -50% 0px", // Trigger when 50% of element is visible
      }
    );

    stepRefs.current.forEach((ref) => ref && observer.observe(ref));

    window.addEventListener("scroll", handleScroll);
    updateLineHeight(); // Initial call

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
  }, [activeSteps]);

  return (
    <div
      style={{
        // backgroundColor: "black",
        overflowX: "hidden",
      }}
      ref={sectionRef}
      className="min-h-screen  py-16 px-4 sm:px-6 lg:px-8"
      id="Process"
    >
      <div className="max-w-7xl mx-auto">
        <div className="relative">
          {/* Timeline Line */}
          <div
  ref={lineRef}
  className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#00ffc7] overflow-hidden hidden md:block"
>
            <div
              className="w-full bg-white origin-top"
              style={{
                backgroundColor: "#fdac00",
                height: `${lineHeight}%`,
                transition: "height 0.17s linear", // Ultra-smooth linear transition
                willChange: "height",
              }}
            />
          </div>

          {/* Timeline Steps */}
          <div className="relative space-y-16">
            {steps.map((step, index) => {
              const isActive = activeSteps.has(index);

              return (
                <div
                key={step.id}
                ref={(el) => (stepRefs.current[index] = el)}
                className={`flex items-center ${
                  step.position === "left" ? "flex-row" : "flex-row-reverse"
                } md:gap-8 gap-4`}
              >
                {/* Content */}
                <div
                  className={`md:w-1/2 w-full ${
                    step.position === "right" ? "md:pl-8" : "md:pr-8"
                  }`}
                >
                 <div
                      className={`rounded-xl p-6 ${step.color} border-2 backdrop-blur-sm shadow-lg`}
                      style={{
                        borderColor: step.borderColor, // Apply the custom border color here
                        borderWidth: "2px", // Ensure the border is visible
                        color:"white",
                        // Mobile-first approach: no transition by default
                        transform: window.innerWidth >= 768 
                          ? (isActive
                            ? "translateX(0) translateY(0)"
                            : step.position === "left"
                            ? "translateX(-100%) translateY(-50%)"
                            : "translateX(100%) translateY(-50%)")
                          : "none",
                        opacity: window.innerWidth >= 768 ? (isActive ? 1 : 0) : 1,
                        transition: window.innerWidth >= 768 
                          ? "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)" 
                          : "none",
                        transitionDelay: window.innerWidth >= 768 
                          ? (isActive ? `${index * 0}s` : "0s")
                          : "0s",
                        willChange: "transform, opacity",
                      }}
                    >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${step.color}`}>
                        {step.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white">
                        {step.title}
                      </h3>
                    </div>
                    <p
                      className="mt-4 text-sm text-gray-400"
                      style={{
                        fontSize: "17px",
                        lineHeight: 1.4,
                      }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              

                  {/* Timeline Point */}
                  <div className="flex items-center justify-center ">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: isActive ? "#fdac00" : "#00ffc7",
                        boxShadow: isActive
                          ? "0 0 10px rgba(255,255,255,0.5)"
                          : "none",
                        transform: isActive ? "scale(1.2)" : "scale(1)",
                        transition:
                          "background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
                        willChange: "background-color, transform, box-shadow",
                      }}
                    />
                  </div>

                  {/* Empty Space for Opposite Side */}
                  <div className="md:w-1/2 hidden md:block" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
   /* Remove horizontal scroll */
          html, body {
            overflow-x: hidden;
          }
          
          @media (min-width: 768px) {
            .process-transition {
              transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
              will-change: transform, opacity;
            }
          }
`}</style>
    </div>
    
  );
};

export default Process;
